import { jsx } from "react/jsx-runtime";
import React, { useRef, useEffect, useState } from "react";
import { bool, string, shape, func, oneOfType, arrayOf, node } from "prop-types";
import cx from "classnames";
import { SkapaFocusLock, Events as FocusLockEvents } from "@ingka/focus-lock-shared";
const propTypes = {
  /** Classname prefix */
  prefix: string,
  /** Enables or disables the focus control feature. */
  locked: bool,
  /** Enables or disables the automatic focus on the first `data-autofocus` attribute flagged element. */
  autoFocus: bool,
  /** Controls if the focus should be returned after deactivation. */
  autoFocusReturn: bool,
  /** Sets the escape hotkey  */
  escapeHotkey: shape({
    key: string,
    shift: bool,
    ctrl: bool
  }),
  /** Cancels any pointer and touch events outside the focus region when the lock is enabled. */
  capturePointerEvents: bool,
  /** Callback on hotkey press */
  onEscaped: func,
  children: oneOfType([
    arrayOf(node),
    node
  ]),
  /** Additional class name to be added to the root element */
  className: string
};
const FocusLock = (props) => {
  const {
    prefix = "",
    locked = false,
    autoFocus = false,
    escapeHotkey,
    capturePointerEvents = true,
    onEscaped,
    autoFocusReturn = true,
    children,
    className,
    ...other
  } = props;
  const ref = useRef();
  const focusTrap = useRef();
  const [escaped, setEscaped] = useState(false);
  const baseClass = `${prefix}skapa-focus-lock`;
  const classes = cx(
    baseClass,
    (!locked || escaped) && `${baseClass}--disabled`,
    className
  );
  const handleEscape = function() {
    if (onEscaped)
      onEscaped();
    setEscaped(true);
  };
  useEffect(() => {
    if (ref.current) {
      if (!focusTrap.current)
        focusTrap.current = new SkapaFocusLock(ref.current);
      const trapInstance = focusTrap.current;
      if (escapeHotkey)
        trapInstance.setDisableKey(escapeHotkey);
      trapInstance.capturePointerEvents = capturePointerEvents;
      trapInstance.autoFocus = autoFocus;
      trapInstance.autoFocusReturn = autoFocusReturn;
      trapInstance.active = locked;
    }
    return () => {
      if (focusTrap.current) {
        focusTrap.current.active = false;
        setEscaped(false);
      }
    };
  }, []);
  useEffect(() => {
    let trapInstance = focusTrap.current;
    if (trapInstance) {
      trapInstance.addEventListener(FocusLockEvents.Escape, handleEscape);
    }
    return () => {
      trapInstance.removeEventListener(FocusLockEvents.Escape, handleEscape);
    };
  }, [onEscaped, focusTrap.current]);
  useEffect(() => {
    if (focusTrap.current)
      focusTrap.current.active = locked;
    setEscaped(false);
  }, [locked]);
  useEffect(() => {
    if (focusTrap.current)
      focusTrap.current.autoFocus = autoFocus;
  }, [autoFocus]);
  useEffect(() => {
    if (focusTrap.current)
      focusTrap.current.autoFocusReturn = autoFocusReturn;
  }, [autoFocusReturn]);
  useEffect(() => {
    if (focusTrap.current)
      focusTrap.current.setDisableKey(escapeHotkey);
  }, [escapeHotkey]);
  useEffect(() => {
    if (focusTrap.current)
      focusTrap.current.capturePointerEvents = capturePointerEvents;
  }, [capturePointerEvents]);
  return /* @__PURE__ */ jsx("div", { ref, className: classes, children, ...other });
};
FocusLock.propTypes = propTypes;
var FocusLock_default = FocusLock;
export {
  FocusLock_default as default
};
